import React, { useState } from 'react';
import { UploadCloudIcon, ExternalLinkIcon, FileSpreadsheetIcon, InfoIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { UploadDropzone } from '../../../components/ui/upload-dropzone';
interface GoodreadsImportFormProps {
  onImportComplete: () => void;
}
export const GoodreadsImportForm: React.FC<GoodreadsImportFormProps> = ({
  onImportComplete
}) => {
  const dk = useDarkClasses();
  const [isParsing, setIsParsing] = useState(false);
  const [parseResult, setParseResult] = useState<{
    total: number;
    valid: number;
    errors: number;
  } | null>(null);
  const handleUploadComplete = (files: any) => {
    setIsParsing(true);
    // Simulate parsing
    setTimeout(() => {
      setParseResult({
        total: 142,
        valid: 138,
        errors: 4
      });
      setIsParsing(false);
    }, 2000);
  };
  const handleAddToQueue = () => {
    onImportComplete();
    setParseResult(null);
  };
  return <div className="space-y-8" data-id="element-10941" data-mp="34edapo4q015i">
      <div className={`p-6 rounded-xl border ${dk.card} ${dk.borderSubtle}`} data-id="element-10942" data-mp="34f5894hvo8yf">
        <h3 className={`font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-10943" data-mp="39dq7b0le8mla">
          <ExternalLinkIcon className="w-5 h-5 mr-2 text-indigo-500" data-id="element-10944" data-mp="2vdfg1wg695zl" />
          How to export from Goodreads
        </h3>
        <ol className={`list-decimal pl-5 space-y-2 text-sm ${dk.textSecondary}`} data-id="element-10945" data-mp="2ze94cqxj0mt0">
          <li data-id="element-10946" data-mp="2lncc517ii7mh">
            Go to{' '}
            <a href="https://www.goodreads.com/review/import" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline" data-id="element-10947" data-mp="2lmb3f3dyxx7x">
              Goodreads Import/Export
            </a>{' '}
            page.
          </li>
          <li data-id="element-10948" data-mp="2gosp0xh6mpsj">
            Click the <strong data-id="element-10949" data-mp="22oroxk8qrme4">Export Library</strong> button at the top right.
          </li>
          <li data-id="element-10950" data-mp="22quoovzp4tw1">
            Wait for the export to generate (this may take a few minutes).
          </li>
          <li data-id="element-10951" data-mp="27pnstxt3f8jj">
            Click the link to download your{' '}
            <code data-id="element-10952" data-mp="d8earecmx181">goodreads_library_export.csv</code> file.
          </li>
          <li data-id="element-10953" data-mp="8gnvqv2r5paa">Upload that file below.</li>
        </ol>
        <div className="mt-4 flex items-start gap-2 text-xs text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg" data-id="element-10954" data-mp="8etaffnkjv5o">
          <InfoIcon className="w-4 h-4 shrink-0 mt-0.5" data-id="element-10955" data-mp="3vmew5dnzd4aq" />
          <p data-id="element-10956" data-mp="3vjso7cx6gmrz">
            Note: Dates from your export will be automatically converted to
            DD/MM/YYYY format on import.
          </p>
        </div>
      </div>

      {!parseResult ? <div className={isParsing ? 'opacity-50 pointer-events-none' : ''} data-id="element-10957" data-mp="r5krl3dsspum">
          <UploadDropzone onUploadComplete={handleUploadComplete} images={[]} maxFiles={1} accept=".csv,text/csv" label="Click to upload or drag and drop" helperText="goodreads_library_export.csv" data-id="element-10958" data-mp="n3z6wvv0v79v" />
          {isParsing && <p className="text-center mt-4 text-sm font-medium text-indigo-600 animate-pulse" data-id="element-10959" data-mp="32z15tyesf6zl">
              Parsing Goodreads CSV...
            </p>}
        </div> : <div className={`p-6 rounded-xl border ${dk.card} ${dk.border}`} data-id="element-10960" data-mp="1neigx6hetf59">
          <div className="flex items-center justify-center mb-6" data-id="element-10961" data-mp="1rej1z9z0kola">
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full" data-id="element-10962" data-mp="1w627347zegom">
              <FileSpreadsheetIcon className="w-8 h-8 text-green-600 dark:text-green-400" data-id="element-10963" data-mp="1w858ekel3w8n" />
            </div>
          </div>

          <h3 className={`text-center font-bold text-lg mb-2 ${dk.textHeading}`} data-id="element-10964" data-mp="263yrkmmt6nze">
            Parse Complete
          </h3>

          <div className="flex justify-center gap-6 mb-8 text-center" data-id="element-10965" data-mp="2b4cphbup4hjp">
            <div data-id="element-10966" data-mp="2f450dv7kazs8">
              <p className={`text-2xl font-bold ${dk.textPrimary}`} data-id="element-10967" data-mp="2f4muomanz3xi">
                {parseResult.total}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-10968" data-mp="grmxx0rsvkfh">
                Total Books
              </p>
            </div>
            <div data-id="element-10969" data-mp="3lllce0l17eqi">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400" data-id="element-10970" data-mp="3ll1rqc3zyc8s">
                {parseResult.valid}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-10971" data-mp="3hn42j9a87q5e">
                Valid
              </p>
            </div>
            <div data-id="element-10972" data-mp="3ql6ay9wahl2p">
              <p className={`text-2xl font-bold ${parseResult.errors > 0 ? 'text-red-500' : dk.textPrimary}`} data-id="element-10973" data-mp="3qmr5w0min613">
                {parseResult.errors}
              </p>
              <p className={`text-xs uppercase tracking-wider ${dk.textMuted}`} data-id="element-10974" data-mp="2xxtqq6dlppck">
                Errors
              </p>
            </div>
          </div>

          <div className="flex justify-center" data-id="element-10975" data-mp="37uye29j6nquy">
            <button onClick={handleAddToQueue} className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors" data-id="element-10976" data-mp="37u6havdunn33">
              Map & Preview {parseResult.valid} Books
            </button>
          </div>
        </div>}
    </div>;
};